import { Alert, App, Popconfirm, Switch } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'

import { crudResourceRegistry } from '@/Core/Services/CrudResourceRegistry'
import { Hint } from '@/Ship/Components/Hint'
import { classNames } from '@/Ship/Helpers/classNames'

import { statusLabel } from '../Contracts/UserOptions'
import { UserStatusManager } from '../Stores/UserStatusManager'

import type { UserCrudContextLike, UserFormContextLike } from '../Contracts/IUser'
import type { UserActivityStatus } from '../Tasks/ChangeUserStatusTask'

import { CardField } from './CardField'
import styles from './UserCard.module.css'

interface Props {
    formContext: UserFormContextLike
    crudContext: UserCrudContextLike | undefined
}

/**
 * Перемикач «Активність» (ADR 038): діє одразу, без «Зберегти».
 *
 * Деактивація — з підтвердженням: після неї людина не входить, а відкриті
 * сесії гаснуть. Активація — без: вона нічого не забирає (`SPEC-005` §7.5).
 * Перемикати може лише той, кому бек дав `update_status` (супер
 * адміністратор, і не на собі — ВП-8); решта бачить стан.
 *
 * Список під карткою перезавантажуємо: деактивований має зникнути з
 * «Активних співробітників» і з'явитися в «Деактивованих».
 */
export const UserStatusField = observer(({ formContext, crudContext }: Props) => {
    const { message } = App.useApp()
    const [manager] = useState(() => new UserStatusManager())

    const values = formContext.values ?? {}
    const uuid = typeof values.uuid === 'string' ? values.uuid : undefined
    const status = typeof values.status === 'string' ? values.status : ''
    const isActive = status === 'active'
    const canChange = uuid !== undefined && values._permissions?.update_status === true

    const apply = async (next: UserActivityStatus): Promise<void> => {
        if (!uuid) return

        const saved = await manager.change(uuid, next)

        if (saved === null) return

        formContext.setInitialValue?.('status', saved)

        const load = crudContext?.listCrudContext?.listContext?.actions?.load

        if (typeof load === 'function') load(true)
        else crudResourceRegistry.reloadListIfExists(crudContext?.resource?.key ?? 'Setting.User')

        void message.success(saved === 'active' ? 'Співробітника активовано.' : 'Співробітника деактивовано.')
    }

    // Перемикач керований: увімкнений не перемикається сам — вимикає його
    // лише «Так» у підтвердженні
    const control = (
        <Switch
            aria-label="Активність"
            checked={isActive}
            loading={manager.isLoading}
            disabled={!canChange}
            {...(!isActive && canChange ? { onChange: (checked: boolean) => void (checked && apply('active')) } : {})}
        />
    )

    return (
        <CardField label="Активність">
            <div className={classNames(styles['stack'])}>
                <div className={classNames(styles['statusRow'])}>
                    {isActive && canChange ? (
                        <Popconfirm
                            title="Деактивувати співробітника?"
                            okText="Так"
                            cancelText="Ні"
                            onConfirm={() => apply('inactive')}
                        >
                            {control}
                        </Popconfirm>
                    ) : (
                        control
                    )}
                    <span className={classNames(styles['statusText'])}>{status ? statusLabel(status) : '—'}</span>
                </div>

                {manager.error ? <Alert type="error" showIcon title={manager.error} /> : null}

                {canChange ? (
                    <Hint id="team.user.status">
                        Деактивований співробітник не зможе увійти, а вже відкритий сеанс завершиться за першої ж
                        його дії. Клієнти, справи й листи, пов'язані з ним, лишаються без змін.
                    </Hint>
                ) : null}
            </div>
        </CardField>
    )
})
